import * as yup from 'yup';
import {
  ReadingStatus,
  ReadingTemplateRoles,
  ReadingType,
} from 'infrastructure/enums';
import { typedObjectEntries } from 'infrastructure/functions/typed-object-entries';

import type { IRpmPatientFormActivity, TFormsReadingType } from './types';

const requiredNumber = yup.number().required();
const requiredString = yup.string().required();
const requiredBool = yup.boolean().required();

// --- Reusable Base Schemas ---
const twoWaySyncDataSchema = {
  attemptedPhoneCall: requiredString,
  attemptedPhoneCallComment: yup.string().when('attemptedPhoneCall', {
    is: 'no-reason',
    then: (schema) => schema.required(),
  }),
  completedPhoneCall: requiredBool,
  uniqueDays: requiredNumber,
};

const followUpAndEscalationSchema = {
  additionalFollowUp: requiredBool,
  additionalFollowUpComment: yup.string().when('additionalFollowUp', {
    is: true,
    then: (schema) => schema.required(),
  }),
  needEscalate: requiredBool,
  needEscalateComment: yup.string().when('needEscalate', {
    is: true,
    then: (schema) => schema.required(),
  }),
};

// --- Risk (Yellow) Schemas ---
const maBloodPressureRiskSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,

  // from IBloodPressureYellowData
  difficultiesWithBPOrPulse: requiredBool,
  difficultiesWithBPOrPulseComment: yup
    .string()
    .when('difficultiesWithBPOrPulse', {
      is: true,
      then: (schema) => schema.required(),
    }),

  waitingForDeviceReplacement: requiredBool,
  waitingForDeviceReplacementList: yup
    .array()
    .of(requiredString)
    .when('waitingForDeviceReplacement', {
      is: true,
      then: (schema) => schema.min(1).required(),
    }),
  waitingForDeviceReplacementComment: yup
    .string()
    .when('waitingForDeviceReplacement', {
      is: false,
      then: (schema) => schema.required(),
    }),
  atRiskNumber: requiredNumber,
  highestSystolic: requiredNumber,
  lowestSystolic: requiredNumber,
  highestDiastolic: requiredNumber,
  lowestDiastolic: requiredNumber,
  highestPulse: requiredNumber,
  lowestPulse: requiredNumber,
  typeofTrend: requiredString,
  highTrends: yup
    .array()
    .of(requiredString)
    .when('typeofTrend', {
      is: 'high',
      then: (schema) => schema.min(1).required(),
    }),
  lowTrends: yup
    .array()
    .of(requiredString)
    .when('typeofTrend', {
      is: 'low',
      then: (schema) => schema.min(1).required(),
    }),
  comparedToLastReview: requiredString,
  comparedToLastReviewTrend: requiredString,
});

const maSpO2RiskSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  spo2DeviceFunctioningProperly: requiredBool,
  spo2DeviceFunctioningProperlyComment: yup
    .string()
    .when('spo2DeviceFunctioningProperly', {
      is: false,
      then: (schema) => schema.required(),
    }),

  didPatientReportSpO2OrPulse: requiredBool,
  didPatientReportSpO2OrPulseComment: yup
    .string()
    .when('didPatientReportSpO2OrPulse', {
      is: true,
      then: (schema) => schema.required(),
    }),

  atRiskNumber: requiredNumber,
  highestSpO2: requiredNumber,
  lowestSpO2: requiredNumber,
  highestPulse: requiredNumber,
  lowestPulse: requiredNumber,
  typeofTrend: requiredString,
  highTrends: yup
    .array()
    .of(requiredString)
    .when('typeofTrend', {
      is: 'high',
      then: (schema) => schema.min(1).required(),
    }),
  lowTrends: yup
    .array()
    .of(requiredString)
    .when('typeofTrend', {
      is: 'low',
      then: (schema) => schema.min(1).required(),
    }),
  comparedToLastReview: requiredString,
  comparedToLastReviewTrend: requiredString,
});

const maGlucoseRiskSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  glucoseDeviceFunctioningProperly: requiredBool,
  glucoseDeviceFunctioningProperlyComment: yup
    .string()
    .when('glucoseDeviceFunctioningProperly', {
      is: false,
      then: (schema) => schema.required(),
    }),

  waitingDeviceReplacement: requiredBool,
  waitingForDeviceReplacementList: yup
    .array()
    .of(requiredString)
    .when('waitingDeviceReplacement', {
      is: true,
      then: (schema) => schema.min(1).required(),
    }),
  waitingForDeviceReplacementComment: yup
    .string()
    .when('waitingDeviceReplacement', {
      is: false,
      then: (schema) => schema.required(),
    }),
  atRiskNumber: requiredNumber,
  highestGlucose: requiredNumber,
  lowestGlucose: requiredNumber,
  typeofTrend: requiredString,
  otherTypeofTrend: yup.string().when('typeofTrend', {
    is: 'other',
    then: (schema) => schema.required(),
  }),
  comparedToLastReview: requiredString,
  comparedToLastReviewTrend: requiredString,
  reviewClinicalDocumentation: requiredBool,
  reviewClinicalDocumentationComment: yup
    .string()
    .when('reviewClinicalDocumentation', {
      is: true,
      then: (schema) => schema.required(),
    }),
});

const maWeightRiskSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  weightReason: requiredString,
  weightReasonComment: yup.string().when('weightReason', {
    is: 'other',
    then: (schema) => schema.required(),
  }),
  waitingDeviceReplacement: requiredBool,
  waitingForDeviceReplacementComment: yup
    .string()
    .when('waitingDeviceReplacement', {
      is: false,
      then: (schema) => schema.required(),
    }),

  didPatientReportWeight: requiredBool,
  didPatientReportWeightComment: yup.string().when('didPatientReportWeight', {
    is: true,
    then: (schema) => schema.required(),
  }),

  atRiskNumber: requiredNumber,
  highestWeight: requiredNumber,
  lowestWeight: requiredNumber,
  typeofTrend: requiredString,
  otherTypeofTrend: yup.string().when('typeofTrend', {
    is: 'other',
    then: (schema) => schema.required(),
  }),
  comparedToLastReview: requiredString,
  comparedToLastReviewTrend: requiredString,
});

// --- Normal (Green) Schemas ---
const maBloodPressureNormalSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  systolicAvg: requiredNumber,
  systolicAvg30days: requiredString,
  systolicAvg90days: requiredString,
  diastolicAvg: requiredNumber,
  diastolicAvg30days: requiredString,
  diastolicAvg90days: requiredString,
  pulseAvg: requiredNumber,
  pulseAvg30days: requiredString,
  pulseAvg90days: requiredString,
  bpAndPulseWithinRange: requiredBool,
  bpAndPulseWithinRangeComment: yup.string(),
});

const maSpO2NormalSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  spo2Avg: requiredNumber,
  spo2Avg30days: requiredString,
  spo2Avg90days: requiredString,
  pulseAvg: requiredNumber,
  pulseAvg30days: requiredString,
  pulseAvg90days: requiredString,
  spo2AndPulseWithinRange: requiredBool,
  spo2AndPulseWithinRangeComment: yup.string(),
});

const maGlucoseNormalSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  glucoseAvg: requiredNumber,
  glucoseAvg30days: requiredString,
  glucoseAvg90days: requiredString,
  glucoseWithinRange: requiredBool,
  glucoseWithinRangeComment: yup.string(),
});

const maWeightNormalSchema = yup.object().shape({
  ...twoWaySyncDataSchema,
  ...followUpAndEscalationSchema,
  weightReason: requiredString,
  weightReasonComment: yup.string().when('weightReason', {
    is: 'other',
    then: (schema) => schema.required(),
  }),
  weightAvg: requiredNumber,
  weightAvg30days: requiredString,
  weightAvg90days: requiredString,
  weightWithinRange: requiredBool,
  weightWithinRangeComment: yup.string(),
});

// --- Free Text Schemas ---
const freeTextSchema = (readingType: ReadingType) =>
  yup.object().shape({
    [readingType]: requiredString,
  });

// -- NURSE (risk) Yellow ---
export const baseNurseSchema = {
  typeofTrend: requiredString,
  typeofTrendComment: yup.string(),
  contactWithPatient: requiredBool,
  noCallMadeRationale: requiredString,
  noCallMadeOtherComment: yup.string().when('noCallMadeRationale', {
    is: 'other',
    then: (schema) => schema.required(),
  }),
  patientAgreeabilityComment: yup.string(),
  patientAgreeability: requiredString,
  patientAcknowledgement: requiredString,

  maTasksDelegated: requiredString,
  maTasksDelegatedList: yup
    .array()
    .of(requiredString)
    .when('maTasksDelegated', {
      is: 'yes',
      then: (schema) => schema.min(1).required(),
    }),
  maTasksDelegatedListComment: yup.string(),
  maTasksDelegatedComment: yup.string(),

  reviewEscalationProtocol: requiredBool,
  reviewEscalationProtocolComment: yup
    .string()
    .when('reviewEscalationProtocol', {
      is: false,
      then: (schema) => schema.required(),
    }),

  emergencyCareAdvised: requiredBool,
  emergencyCareAdvisedComment: yup.string().when('emergencyCareAdvised', {
    is: false,
    then: (schema) => schema.required(),
  }),

  potentialContributingFactors: yup
    .array()
    .of(requiredString)
    .min(1)
    .required(),
  potentialContributingFactorsComment: yup.string(),

  decisionMakingNextSteps: yup.array().of(requiredString).min(1).required(),
  escalateToProviderReason: yup.string().when('decisionMakingNextSteps', {
    is: (arr?: string[]) =>
      arr?.some((i) => i.includes('<escalateToProviderReason>')),
    then: (schema) => schema.min(1).required(),
  }),
  notifyPharmacyReason: yup.string().when('decisionMakingNextSteps', {
    is: (arr?: string[]) =>
      arr?.some((i) => i?.includes('<notifyPharmacyReason>')),
    then: (schema) => schema.required(),
  }),
  scheduleFollowUpCallValue: yup.number().when('decisionMakingNextSteps', {
    is: (arr?: string[]) => arr?.some((i) => i?.includes('<value>')),
    then: (schema) => schema.required(),
  }),

  patientEducationProvided: yup.array().of(requiredString).min(1).required(),
  patientEducationProvidedComment: yup.string(),

  digitalEngagementEducationMethod: requiredString,
  digitalEngagementEducationMethodComment: yup.string(),
};

// NURSE Blood Pressure (risk) Yellow
export const nurseBpYellowSchema = yup.object().shape({
  ...baseNurseSchema,
  highestSystolic: requiredNumber,
  lowestSystolic: requiredNumber,
  highestDiastolic: requiredNumber,
  lowestDiastolic: requiredNumber,
  highestPulse: requiredNumber,
  lowestPulse: requiredNumber,

  severeHeadache: requiredBool,
  chestPainPressure: requiredBool,
  newWorseningShortnessOfBreath: requiredBool,
  confusionAlteredMentalStatus: requiredBool,
  visionChanges: requiredBool,
  severeDizzinessLossOfBalanceSyncope: requiredBool,
  newLimbWeaknessNumbness: requiredBool,
  palpitations: requiredBool,
  dizzinessLightheadedness: requiredBool,
  fatigueWeakness: requiredBool,
  swellingInLegsFeet: requiredBool,
  nauseaVomiting: requiredBool,
  difficultyUrinatingUnusualUrine: requiredBool,
  adherenceToBPMedAsPrescribed: requiredBool,
  recentMedHealthChanges: requiredBool,
  clinicalAssessmentComment: yup.string(),

  nurseClinicalSummaryAndRationale: requiredString,
  followUpPlan: requiredString,
  followUpPlanDate: yup.date().when('followUpPlan', {
    is: 'yes',
    then: (schema) => schema.required(),
  }),
});

// NURSE SpO2 (risk) Yellow
export const nurseSpo2YellowSchema = yup.object().shape({
  ...baseNurseSchema,
  highestSpo2: requiredNumber,
  lowestSpo2: requiredNumber,
  highestPulse: requiredNumber,
  lowestPulse: requiredNumber,

  shortnessOfBreathAtRest: requiredBool,
  chestPainPressure: requiredBool,
  confusionAlteredMentalStatus: requiredBool,
  blueLipsNailsCyanosis: requiredBool,
  severeWeaknessFatigue: requiredBool,
  unableToSpeakExcessiveBreathlessness: requiredBool,
  coughFever: requiredBool,
  shortnessOfBreath: requiredBool,
  mildFatigue: requiredBool,
  sleepDisturbances: requiredBool,
  useOfOxygenAsPrescribed: requiredBool,
  recentIllnessHospitalization: requiredBool,
  clinicalAssessmentComment: yup.string(),
});

// NURSE Glucose (risk) Yellow
export const nurseGlucoseYellowSchema = yup.object().shape({
  ...baseNurseSchema,
  highestGlucose: requiredNumber,
  lowestGlucose: requiredNumber,

  lossOfConsciousness: requiredBool,
  seizure: requiredBool,
  inabilityToEatDrink: requiredBool,
  persistentVomitingDiarrhea: requiredBool,
  shortnessOfBreath: requiredBool,
  severeAbdominalPain: requiredBool,
  profoundWeakness: requiredBool,
  shakinessSweating: requiredBool,
  blurredVision: requiredBool,
  extremeThirstHunger: requiredBool,
  frequentUrination: requiredBool,
  adherenceToDiabetesMedsInsulin: requiredBool,
  sickDayOrDietaryChange: requiredBool,
  clinicalAssessmentComment: yup.string(),

  nurseClinicalSummaryAndRationale: requiredString,
  followUpPlan: requiredString,
  followUpPlanDate: yup.date().when('followUpPlan', {
    is: 'yes',
    then: (schema) => schema.required(),
  }),
});

// NURSE Weight (risk) Yellow
export const nurseWeightYellowSchema = yup.object().shape({
  ...baseNurseSchema,
  reasonForWeightMonitoring: requiredString,
  reasonForWeightMonitoringOther: yup
    .string()
    .when('reasonForWeightMonitoring', {
      is: 'other',
      then: (schema) => schema.required(),
    }),

  highestWeight: requiredNumber,
  lowestWeight: requiredNumber,
  avgWeight: requiredNumber,

  rapidWeightGain: requiredBool,
  rapidWeightLoss: requiredBool,
  swellingInLegsFeetAbdomen: requiredBool,
  shortnessOfBreathNewOrWorsening: requiredBool,
  difficultyBreathing: requiredBool,
  awakeningShortOfBreath: requiredBool,
  fatigue: requiredBool,
  dizzinessLightheadedness: requiredBool,
  confusionMentalStatusChange: requiredBool,
  chestPainPressure: requiredBool,
  frequentUrination: requiredBool,
  nauseaDecreasedAppetite: requiredBool,
  mildSwellingEdema: requiredBool,
  mildDifficultyBreathing: requiredBool,
  numberPillowsUsedForSleepIncreased: requiredBool,
  medicationAdherence: requiredBool,
  dietarySodiumFluidIntakeChanges: requiredBool,
  recentPhysicalActivityChanges: requiredBool,
  recentIllnessInfection: requiredBool,
  clinicalAssessmentComment: yup.string(),

  nurseClinicalSummaryAndRationale: requiredString,
  followUpPlan: requiredString,
  followUpPlanDate: yup.date().when('followUpPlan', {
    is: 'yes',
    then: (schema) => schema.required(),
  }),
});

const maRiskSchema = yup
  .object()
  .nullable()
  .shape({
    [ReadingType.BloodPressure]: maBloodPressureRiskSchema,
    [ReadingType.SpO2]: maSpO2RiskSchema,
    [ReadingType.BloodGlucose]: maGlucoseRiskSchema,
    [ReadingType.Weight]: maWeightRiskSchema,
    [ReadingType.Temperature]: freeTextSchema(ReadingType.Temperature),
    [ReadingType.Pulse]: freeTextSchema(ReadingType.Pulse),
    [ReadingType.SleepingMat]: freeTextSchema(ReadingType.SleepingMat),
    [ReadingType.PeakFlow]: freeTextSchema(ReadingType.PeakFlow),
  });

const maNormalSchema = yup
  .object()
  .nullable()
  .shape({
    [ReadingType.BloodPressure]: maBloodPressureNormalSchema,
    [ReadingType.SpO2]: maSpO2NormalSchema,
    [ReadingType.BloodGlucose]: maGlucoseNormalSchema,
    [ReadingType.Weight]: maWeightNormalSchema,
    [ReadingType.Temperature]: freeTextSchema(ReadingType.Temperature),
    [ReadingType.Pulse]: freeTextSchema(ReadingType.Pulse),
    [ReadingType.SleepingMat]: freeTextSchema(ReadingType.SleepingMat),
    [ReadingType.PeakFlow]: freeTextSchema(ReadingType.PeakFlow),
  });

const nurseRiskSchema = yup
  .object()
  .nullable()
  .shape({
    [ReadingType.BloodPressure]: nurseBpYellowSchema,
    [ReadingType.SpO2]: nurseSpo2YellowSchema,
    [ReadingType.BloodGlucose]: nurseGlucoseYellowSchema,
    [ReadingType.Weight]: nurseWeightYellowSchema,
    [ReadingType.Temperature]: freeTextSchema(ReadingType.Temperature),
    [ReadingType.Pulse]: freeTextSchema(ReadingType.Pulse),
    [ReadingType.SleepingMat]: freeTextSchema(ReadingType.SleepingMat),
    [ReadingType.PeakFlow]: freeTextSchema(ReadingType.PeakFlow),
  });

const nurseNormalSchema = yup
  .object()
  .nullable()
  .shape({
    [ReadingType.BloodPressure]: freeTextSchema(ReadingType.BloodPressure),
    [ReadingType.SpO2]: freeTextSchema(ReadingType.SpO2),
    [ReadingType.BloodGlucose]: freeTextSchema(ReadingType.BloodGlucose),
    [ReadingType.Weight]: freeTextSchema(ReadingType.Weight),
    [ReadingType.Temperature]: freeTextSchema(ReadingType.Temperature),
    [ReadingType.Pulse]: freeTextSchema(ReadingType.Pulse),
    [ReadingType.SleepingMat]: freeTextSchema(ReadingType.SleepingMat),
    [ReadingType.PeakFlow]: freeTextSchema(ReadingType.PeakFlow),
  });

export const nurseValidationSchema = yup.object().shape({
  [ReadingStatus.Risk]: nurseRiskSchema,
  [ReadingStatus.Normal]: nurseNormalSchema, // add maNormalSchema
  [ReadingStatus.Critical]: yup.object().nullable(),
});

export const maValidationSchema = yup.object().shape({
  [ReadingStatus.Risk]: maRiskSchema,
  [ReadingStatus.Normal]: maNormalSchema,
  [ReadingStatus.Critical]: yup.object().nullable(),
});

export const roleValidationSchema = {
  [ReadingTemplateRoles.RegisterNurse]: nurseValidationSchema,
  [ReadingTemplateRoles.MedicalAssistant]: maValidationSchema,
};

export const getValidationSchemaByData = (
  arr: IRpmPatientFormActivity[],
  templateRole: ReadingTemplateRoles,
) => {
  const validationSchema = roleValidationSchema[templateRole];
  const fieldsByStatusEntries = typedObjectEntries(
    validationSchema.fields,
  ).filter(
    (
      entry,
    ): entry is [
      ReadingStatus,
      (typeof validationSchema.fields)[ReadingStatus],
    ] => {
      const s = entry[0];
      return arr.some((a) => a.globalStatus === s);
    },
  );

  const statusFieldsByType = fieldsByStatusEntries.map(([status, schema]) => {
    const filteredByTypeEntries = typedObjectEntries(schema.fields).filter(
      (
        entry,
      ): entry is [
        TFormsReadingType,
        (typeof schema.fields)[TFormsReadingType],
      ] =>
        arr.some(
          (a) => a.readingType === entry[0] && a.globalStatus === status,
        ),
    );

    const fieldsByTypeShape = Object.fromEntries(filteredByTypeEntries);

    return [status, yup.object().shape(fieldsByTypeShape)];
  });

  const filteredShape = Object.fromEntries(
    statusFieldsByType,
  ) as typeof validationSchema.__outputType;

  return yup.object().shape(filteredShape);
};
